import React, { ReactNode } from "react";
import { twMerge } from "tailwind-merge";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode; // Button text or content
  size?: "sm" | "md" | "lg"; // Button size
  variant?: "primary" | "outline" | "destructive" | "ghost" | "danger" | "secondary" | "default" | "success" | "warning" | "subtle" | "secondary-outline" | "filled" | "none"; // Button variant
  startIcon?: ReactNode; // Icon before the text
  endIcon?: ReactNode; // Icon after the text
  asChild?: boolean; // Support Radix-like asChild pattern
  isLoading?: boolean; // Loading state
  loading?: boolean; // Alias for isLoading (back-compat)
}

const Button: React.FC<ButtonProps> = ({
  children,
  size = "md",
  variant = "primary",
  startIcon,
  endIcon,
  className = "",
  asChild = false,
  isLoading = false,
  loading,
  disabled,
  ...props
}) => {
  const isButtonLoading = isLoading || loading;
  const isDisabled = Boolean(disabled || isButtonLoading);
  // Size Classes
  const sizeClasses = {
    sm: "px-4 py-3 text-sm",
    md: "px-5 py-3.5 text-sm",
    lg: "px-6 py-4 text-base",
  };

  // Variant Classes
  const variantClasses = {
    primary:
      "bg-brand-gradient text-white shadow-sm hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed dark:shadow-none dark:border dark:border-brand-500/30",
    default:
      "bg-brand-gradient text-white shadow-sm hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed dark:shadow-none dark:border dark:border-brand-500/30",
    secondary:
      "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-white/[0.03] dark:text-gray-300 dark:hover:bg-white/[0.06]",
    outline:
      "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700 dark:hover:bg-white/[0.03] dark:hover:text-gray-300",
    destructive:
      "bg-red-600 text-white shadow-sm hover:bg-red-700 disabled:bg-red-300 dark:bg-red-600 dark:hover:bg-red-700 dark:disabled:bg-red-800",
    // "danger" is an alias for destructive (used across older screens)
    danger:
      "bg-red-600 text-white shadow-sm hover:bg-red-700 disabled:bg-red-300 dark:bg-red-600 dark:hover:bg-red-700 dark:disabled:bg-red-800",
    // "ghost" is a minimal button (used for icon/text actions)
    ghost:
      "bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/[0.06]",
    // Additional variants (map to existing styles)
    success: "bg-green-600 text-white shadow-sm hover:bg-green-700 disabled:opacity-50 dark:bg-green-600 dark:hover:bg-green-700",
    warning: "bg-amber-600 text-white shadow-sm hover:bg-amber-700 disabled:opacity-50 dark:bg-amber-600 dark:hover:bg-amber-700",
    subtle: "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/[0.03] dark:text-gray-300 dark:hover:bg-white/[0.06]",
    "secondary-outline": "bg-transparent text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:ring-gray-700 dark:text-gray-400 dark:hover:bg-white/[0.03]",
    filled: "bg-brand-gradient text-white shadow-sm hover:brightness-95 disabled:opacity-50 dark:shadow-none dark:border dark:border-brand-500/30",
    none: "bg-transparent hover:bg-gray-100 dark:hover:bg-white/[0.06]",
  };

  // Use CSS classes for icon color instead of React cloning. A caller that sets
  // the text color (a pale gym color needs dark text) gets icons in that color.
  const iconColorClass = !props.style?.color && (variant === 'primary' || variant === 'default' || variant === 'destructive' || variant === 'danger') ? 'text-white' : '';

  const buttonClasses = twMerge(
    "inline-flex items-center justify-center font-medium gap-2 rounded-lg transition disabled:cursor-not-allowed disabled:opacity-50",
    sizeClasses[size],
    variantClasses[variant as keyof typeof variantClasses] || variantClasses.primary,
    isButtonLoading && "opacity-70 cursor-not-allowed",
    className
  );

  const content = (
    <>
      {isButtonLoading ? (
        <span className="flex items-center animate-spin">
          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
      ) : startIcon && (
        <span className={`flex items-center ${iconColorClass}`}>{startIcon}</span>
      )}
      {children}
      {!isButtonLoading && endIcon && (
        <span className={`flex items-center ${iconColorClass}`}>{endIcon}</span>
      )}
    </>
  );

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      className: twMerge(buttonClasses, (children.props as any).className),
      ...props,
      disabled: isDisabled,
    });
  }

  return (
    <button
      className={buttonClasses}
      {...props}
      disabled={isDisabled}
    >
      {content}
    </button>
  );
};

export default Button;


